import {
  type AssetManifest,
  footprintOf,
  type PropPlacement,
  PROPS,
  TILE_SIZE,
  type ZoneDefinition,
} from '@shefira/shared';
import type Phaser from 'phaser';

/**
 * Draw a zone's props: trees, bushes, rocks, flowers.
 *
 * Every prop stands with its base on the bottom edge of its tile, centred.
 * A blocking prop (tree, rock) is sorted with the characters by its base,
 * like them: walking north of a tree passes behind its canopy. Ground cover
 * (flowers, reeds) never hides anyone, so it sits under every character.
 *
 * Plain images, thousands of them in the open world: the camera culls what
 * is off screen, and they all share one atlas, so it is one draw call. The
 * few animated ones are sprites playing a loop registered by the scene.
 */
export interface PropsOptions {
  scene: Phaser.Scene;
  zone: ZoneDefinition;
  manifest: AssetManifest;
}

/** Above the ground layer, below anything that walks. */
const COVER_DEPTH = -500;

/** A prop's image: a still one, or a sprite looping its clip. */
export type PropImage = Phaser.GameObjects.Image | Phaser.GameObjects.Sprite;

/**
 * Every prop of the zone, drawn; the images line up with `zone.props`
 * (undefined where the manifest has no sprite), so the world editor
 * (ADR 0022) can move them.
 */
export function drawProps({ scene, zone, manifest }: PropsOptions): (PropImage | undefined)[] {
  return zone.props.map((placed) => drawProp(scene, manifest, placed));
}

export function drawProp(
  scene: Phaser.Scene,
  manifest: AssetManifest,
  placed: PropPlacement,
): PropImage | undefined {
  const definition = PROPS[placed.prop];
  const sprite = definition === undefined ? undefined : manifest.sprites[definition.sprite];
  if (definition === undefined || sprite === undefined) return undefined;
  // An animated prop (the portal's vortex, ADR 0017) loops its clip.
  const clip = `${definition.sprite}/idle`;
  const image =
    manifest.sheets[definition.sprite] !== undefined && scene.anims.exists(clip)
      ? scene.add.sprite(0, 0, sprite.atlas, sprite.frame).play(clip)
      : scene.add.image(0, 0, sprite.atlas, sprite.frame);
  image.setOrigin(0, 0);
  placeProp(image, placed);
  return image;
}

/** Stand a prop's image on its tile, and sort it with the characters. */
export function placeProp(image: PropImage, placed: PropPlacement): void {
  const definition = PROPS[placed.prop];
  if (definition === undefined) return;
  // Centred on its footprint, top-left corner on whole pixels (CLAUDE.md
  // rule 6): an odd-width frame centred exactly would sit on a half pixel.
  const box = footprintOf(placed);
  const middle = ((box.x0 + box.x1 + 1) * TILE_SIZE) / 2;
  const left = Math.round(middle - image.width / 2);
  const base = (placed.y + 1) * TILE_SIZE;
  // A bridge lies across its whole footprint; everything else stands on its base.
  const top =
    definition.anchor === 'centre'
      ? Math.round(((box.y0 + box.y1 + 1) * TILE_SIZE) / 2 - image.height / 2)
      : base - image.height;
  image
    .setPosition(left, top)
    .setFlipX(placed.flip)
    // The same painter's order as the characters: the lower base is nearer.
    // A hole in the ground (the crevasse) blocks but lies under everyone.
    .setDepth(definition.blocks && !definition.flat ? base : COVER_DEPTH);
}
